import { useRef } from 'react';

import type { Dictionary } from 'ramda';

import { MARGIN_MODE } from 'constants/enums';
import {
  aggUnrealisedPnLFromApiSelector,
  loadingOpenPositionsSelector,
} from 'selectors/holdingsSelectors';
import {
  userMarginModeSelector,
  userPreferencesSelector,
} from 'selectors/otherSelectors';
import {
  openPositionMarkPriceSelectorById,
  spotPriceSelectorBySymbol,
} from 'selectors/priceSelectors';
import { selectIsAuthChannelFailed } from 'selectors/user';
import { useAppSelector } from 'storeHooks';
import { OpenPosition } from 'types/IOpenPosition';
import { Preferences } from 'types/IUser';
import { IBalance } from 'types/IWallet';

const isSpotAndMarkPriceLoadedForAllOpenPositions = (
  openPositionsList: OpenPosition[]
) => {
  return openPositionsList.every(position => {
    const productId = position?.product?.id;
    const spotIndexSymbol = position?.product?.spot_index?.symbol;
    if (!spotIndexSymbol || !productId) {
      return false;
    }

    const spotPrice = spotPriceSelectorBySymbol(spotIndexSymbol);

    const markPriceObj = openPositionMarkPriceSelectorById(productId.toString()) as any;

    return spotPrice !== undefined && markPriceObj?.mark_price !== undefined;
  });
};

const checkShouldShowContentForAccountEquity = ({
  openPositionsList,
  marginMode,
  openPositionsLoading,
  balance,
  userPreferences,
  isAuthChannelDisconnected,
  aggUnrealisedPnLFromApi,
}: {
  openPositionsList: OpenPosition[];
  marginMode: MARGIN_MODE;
  openPositionsLoading: boolean;
  portfolioUpl?: number;
  balance: Dictionary<IBalance>;
  userPreferences: Preferences;
  isAuthChannelDisconnected: boolean;
  aggUnrealisedPnLFromApi: number | null;
}) => {
  if (!Object.keys(balance).length || openPositionsLoading) {
    return false;
  }

  if (userPreferences?.minimal_updates || marginMode === MARGIN_MODE.PORTFOLIO) {
    return true;
  }

  if (isAuthChannelDisconnected && aggUnrealisedPnLFromApi !== null) return true;

  return isSpotAndMarkPriceLoadedForAllOpenPositions(openPositionsList);
};

const useCheckShouldShowContentForAccountEquity = (
  openPositionsList: OpenPosition[],
  balance: Dictionary<IBalance>
) => {
  // when we have already displayed means mark prices and spot prices were loaded.
  //  so dont want to do function checks again as issue comes on first load and value displayed is not correct only
  const displayedValueToUser = useRef(false);

  const userPreferences = useAppSelector(userPreferencesSelector);

  const openPositionsLoading = useAppSelector(loadingOpenPositionsSelector);

  const isAuthChannelDisconnected = useAppSelector(selectIsAuthChannelFailed);
  const aggUnrealisedPnLFromApi = useAppSelector(aggUnrealisedPnLFromApiSelector);
  const marginMode = useAppSelector(userMarginModeSelector);
  if (displayedValueToUser.current) {
    return true;
  }

  const shouldShowContent = checkShouldShowContentForAccountEquity({
    openPositionsList,
    marginMode,
    openPositionsLoading,
    balance,
    userPreferences,
    isAuthChannelDisconnected,
    aggUnrealisedPnLFromApi,
  });

  if (shouldShowContent) {
    displayedValueToUser.current = true;
  }

  return shouldShowContent;
};

export { useCheckShouldShowContentForAccountEquity };
